import type { BimChapterEnum, ConstructionTypeData } from 'gql/graphql';
import { CostGroupEnum } from 'gql/graphql';
import { WorkOrderChapters } from 'pages/Project/UerProject/WorkOrder/workorderTypes';

import type { ColDef, ColGroupDef, GridApi } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { runOn } from '../utils/runOnRefExists';

type useScrollToWithNavigationInfoProps<ObjType> = {
    constructionTypeSetter: (id: number) => void;
    objectSetter: (
        {
            subObjectId,
            objectId,
            chapter,
        }: {
            chapter: any;
            objectId: number;
            subObjectId: number | undefined;
        },
        newConstructionTypeId?: number,
    ) => void;
    objects: ObjType[] | undefined | null;
    tableApi: AgGridReact | null | undefined;
    expandColumnsHandler?: () => void;
    isNew?: boolean;
};

export type TActNavInfoState = {
    actNavInfo: {
        actId: number;
        constructionTypeId: number;
        workOrderObjectId: number;
        costGroup: CostGroupEnum;
        chapter?: BimChapterEnum;
        allActIds?: number[];
    };
};

const colIdFinder = (col: ColDef | ColGroupDef): string | undefined => {
    if ('colId' in col) {
        return col.colId;
    }

    if ('children' in col) {
        return colIdFinder(col.children.filter((c) => 'hide' in c && !c.hide)[0] ?? {});
    }

    return undefined;
};

const columnColIdFinder = (
    columns: ((ColDef & { id?: number }) | ColGroupDef<any>)[] | undefined,
    actId: number | null,
): string | undefined => {
    if (!columns || !actId) {
        return undefined;
    }

    for (let i = 0; i < columns.length; i++) {
        const col = columns[i];

        if ('id' in col && col.id === actId) {
            return colIdFinder(col);
        }

        if ('children' in col) {
            const checkChild = columnColIdFinder(col.children, actId);

            if (checkChild) {
                return checkChild;
            }
        }
    }

    return undefined;
};

export const useScrollToWithNavigationInfo = <
    T extends {
        id: number;
        constructionType: ConstructionTypeData;
        rootObjectId?: number;
        isLeaf?: boolean;
    },
>({
    constructionTypeSetter,
    objectSetter,
    objects,
    tableApi,
    expandColumnsHandler,
    isNew = false,
}: useScrollToWithNavigationInfoProps<T>) => {
    const isValidRef = useRef(true);
    const scrollActIdRef = useRef<null | number>(null);

    const navigate = useNavigate();
    const { pathname, state } = useLocation();

    const resetRefsState = useCallback(() => {
        scrollActIdRef.current = null;
        isValidRef.current = true;
    }, []);

    const navigateWithStateReset = useCallback(() => {
        isValidRef.current = false;

        setTimeout(() => {
            navigate(pathname, { replace: true, state: null });
        }, 1000);
    }, [navigate, pathname]);

    const { actNavInfo } = (state as TActNavInfoState) || {};

    const { actId, constructionTypeId, workOrderObjectId, costGroup, chapter } = actNavInfo || {};

    useEffect(() => {
        if (actNavInfo && isValidRef.current) {
            scrollActIdRef.current = actId;

            if (!isNew && constructionTypeId && workOrderObjectId && objects) {
                const subObjects = objects?.filter(
                    ({ constructionType, rootObjectId, isLeaf }) =>
                        rootObjectId === workOrderObjectId && constructionType.id === constructionTypeId && isLeaf,
                );

                const isAreThereSubObjects = !!subObjects.length;

                const isOtherNewChapter = costGroup === CostGroupEnum.Others;
                const notOtherNewSubObjectId = isAreThereSubObjects ? subObjects[0]?.id : workOrderObjectId;
                const newSubObjectId = isOtherNewChapter ? undefined : notOtherNewSubObjectId;

                const isChapterInState = Boolean(chapter);
                const notChapterInState = isOtherNewChapter ? WorkOrderChapters.OTHERS : WorkOrderChapters.CHAPTER1_7;
                const newChapter = isChapterInState ? chapter : notChapterInState;

                constructionTypeSetter(constructionTypeId);
                setTimeout(() => {
                    objectSetter(
                        {
                            chapter: newChapter,
                            objectId: workOrderObjectId,
                            subObjectId: newSubObjectId,
                        },
                        constructionTypeId,
                    );
                }, 0);
            } else if (isNew && constructionTypeId && objects) {
                constructionTypeSetter(constructionTypeId);
            }

            if (objects) {
                navigateWithStateReset();
            }
        }
    }, [
        actId,
        actNavInfo,
        chapter,
        constructionTypeId,
        constructionTypeSetter,
        costGroup,
        isNew,
        navigate,
        navigateWithStateReset,
        objectSetter,
        objects,
        pathname,
        workOrderObjectId,
    ]);

    const eventCallback = useCallback(
        ({ api }: { api: GridApi }) => {
            setTimeout(() => {
                const currentColumns = api?.getColumnDefs();

                const actColumnName = columnColIdFinder(currentColumns, scrollActIdRef?.current);

                if (expandColumnsHandler) {
                    expandColumnsHandler();
                }

                if (scrollActIdRef?.current && actColumnName) {
                    resetRefsState();

                    api?.ensureColumnVisible(`${actColumnName}`, 'middle');
                    api?.removeEventListener('newColumnsLoaded', eventCallback);
                }
            }, 500);
        },
        [expandColumnsHandler, resetRefsState],
    );

    runOn(
        () => scrollActIdRef?.current && tableApi?.api?.addEventListener,
        () => scrollActIdRef?.current && tableApi?.api?.addEventListener?.('newColumnsLoaded', eventCallback),
        5,
        1000,
    );
};
